import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Mic, Car, ArrowLeft, Loader2, CheckCircle, ClipboardList, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { normalizarPlaca as normalizarPlacaIA } from "@/lib/aiAssistantService"
import { validarPlaca } from "@/lib/fipeService"
import { buscarVeiculoPorPlaca, type VeiculoResumo } from "@/lib/veiculoSearch"
import { criarAvaliacaoParaVeiculo } from "@/lib/avaliacoes"
import { cn } from "@/lib/utils"
import { toast } from "sonner"

// Rotas onde o botão global aparece
const ROTAS_COM_BOTAO_GLOBAL = ["/avaliacoes"]

// Rotas que já têm botão flutuante próprio — não renderizar aqui
const ROTAS_COM_BOTAO_PROPRIO = ["/avaliacao/", "/diagnostico/"]

type Etapa =
  | "idle"
  | "capturando"
  | "processando"
  | "aguardando_acao"
  | "buscando_veiculo"
  | "confirmando_veiculo"
  | "criando_avaliacao"
  | "manual"

export function AssistenteVozGlobal() {
  const location = useLocation()
  const navigate = useNavigate()
  const { loja } = useAuth()
  const vertical = useTipoOperacao()
  const speech = useSpeechRecognition()

  const [etapa, setEtapa] = useState<Etapa>("idle")
  const [placaConfirmada, setPlacaConfirmada] = useState<string | null>(null)
  const [, setAcaoPendente] = useState<"cadastrar" | "avaliar" | null>(null)
  const [veiculoEncontrado, setVeiculoEncontrado] = useState<VeiculoResumo | null>(null)
  const [placaManual, setPlacaManual] = useState("")
  const [mensagemErro, setMensagemErro] = useState<string | null>(null)

  const ultimoTextoRef = useRef("")

  const rotaAtual = location.pathname
  const temBotaoProprio = ROTAS_COM_BOTAO_PROPRIO.some(r => rotaAtual.includes(r))
  const rotaPermitida = ROTAS_COM_BOTAO_GLOBAL.some(r => rotaAtual === r || rotaAtual.startsWith(r))

  // Hooks SEMPRE executados — retorno condicional só no render, após todos os hooks
  useEffect(() => {
    const texto = speech.transcript || speech.interimTranscript
    if (texto) ultimoTextoRef.current = texto
  }, [speech.transcript, speech.interimTranscript])

  useEffect(() => {
    if (temBotaoProprio) return
    if (etapa !== "capturando") return
    if (speech.state !== "idle") return
    const texto = ultimoTextoRef.current.trim()
    if (!texto) return
    void processarTranscricao(texto)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.state, etapa, temBotaoProprio])

  const fechar = () => {
    speech.stop()
    speech.reset()
    ultimoTextoRef.current = ""
    setEtapa("idle")
    setPlacaConfirmada(null)
    setAcaoPendente(null)
    setVeiculoEncontrado(null)
    setPlacaManual("")
    setMensagemErro(null)
  }

  const iniciarCaptura = () => {
    ultimoTextoRef.current = ""
    setPlacaConfirmada(null)
    setAcaoPendente(null)
    setVeiculoEncontrado(null)
    setMensagemErro(null)
    setPlacaManual("")
    speech.reset()
    setEtapa("capturando")
    speech.start()
  }

  const processarTranscricao = async (texto: string) => {
    setEtapa("processando")
    try {
      const result = await normalizarPlacaIA(texto)
      if (!result.placa || !validarPlaca(result.placa)) {
        setMensagemErro("Não entendi a placa. Tente de novo ou digite.")
        setPlacaManual(texto)
        setEtapa("manual")
        return
      }
      setPlacaConfirmada(result.placa)
      setEtapa("aguardando_acao")
    } catch {
      setMensagemErro("Erro ao processar voz.")
      setEtapa("manual")
    }
  }

  const executarAcao = async (placa: string, acao: "cadastrar" | "avaliar") => {
    if (acao === "cadastrar") {
      fechar()
      navigate("/novo-veiculo", { state: { placaPreenchidaPorIA: placa } })
      return
    }

    if (!loja?.id) { toast.error("Sessão inválida."); return }
    setAcaoPendente("avaliar")
    setEtapa("buscando_veiculo")
    try {
      const veiculo = await buscarVeiculoPorPlaca(placa, loja.id)
      if (!veiculo) {
        setMensagemErro(`Placa ${placa} não cadastrada. Cadastre o veículo primeiro.`)
        setEtapa("manual")
        return
      }
      setVeiculoEncontrado(veiculo)
      setEtapa("confirmando_veiculo")
    } catch {
      toast.error("Erro ao buscar veículo.")
      fechar()
    }
  }

  const confirmarVeiculo = async (veiculo: VeiculoResumo) => {
    if (!loja?.id) return
    setEtapa("criando_avaliacao")
    try {
      const { id, reusedExisting } = await criarAvaliacaoParaVeiculo({
        lojaId: loja.id,
        veiculoId: veiculo.id,
      })
      if (reusedExisting) toast.info("Retomando avaliação em andamento.")
      fechar()
      navigate(`/avaliacao/${id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar avaliação.")
      fechar()
    }
  }

  const tentarPlacaManual = (acao?: "cadastrar" | "avaliar") => {
    const limpa = placaManual.toUpperCase().replace(/[^A-Z0-9]/g, "")
    if (!validarPlaca(limpa)) {
      toast.error("Placa inválida. Use ABC1234 ou ABC1D23.")
      return
    }
    setPlacaConfirmada(limpa)
    if (acao) {
      void executarAcao(limpa, acao)
    } else {
      setEtapa("aguardando_acao")
    }
  }

  // ── Render ──────────────────────────────────────────────────

  // Só mostrar nas rotas permitidas; páginas com botão próprio também ficam fora
  if (temBotaoProprio || !rotaPermitida) return null

  if (etapa === "idle") {
    if (!speech.isSupported) return null
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={iniciarCaptura}
          aria-label="Assistente de voz"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg transition-transform hover:scale-105"
        >
          <Mic className="h-6 w-6 text-primary-foreground" />
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Card principal */}
      <div className="w-80 rounded-xl bg-background/95 shadow-xl ring-1 ring-border backdrop-blur">

        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-medium text-muted-foreground">Assistente IA</p>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={fechar}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-4 space-y-4">

          {/* CAPTURANDO */}
          {etapa === "capturando" && (
            <>
              <div className="flex flex-col items-center gap-3 py-2">
                <p className="text-sm font-medium text-center">Fale a placa do veículo</p>
                <button
                  onClick={speech.stop}
                  aria-label="Parar gravação"
                  className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 shadow-md animate-pulse"
                >
                  <Mic className="h-6 w-6 text-white" />
                </button>
                <p className="text-xs text-muted-foreground">Ouvindo... toque para parar</p>
              </div>
              {(speech.transcript || speech.interimTranscript) && (
                <p className="rounded-md bg-muted/40 p-2 text-sm text-center">
                  {speech.transcript || speech.interimTranscript}
                </p>
              )}
              <Input
                placeholder="Ou digite a placa"
                value={placaManual}
                onChange={e => setPlacaManual(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                maxLength={7}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              {placaManual.trim() && (
                <Button className="w-full" size="sm" onClick={() => tentarPlacaManual()}>
                  Usar essa placa
                </Button>
              )}
            </>
          )}

          {/* PROCESSANDO */}
          {etapa === "processando" && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Entendendo a placa...</span>
            </div>
          )}

          {/* AGUARDANDO AÇÃO */}
          {etapa === "aguardando_acao" && placaConfirmada && (
            <>
              <div className="rounded-lg border-2 border-primary bg-primary/5 py-3 text-center">
                <p className="text-2xl font-bold tracking-widest">{placaConfirmada}</p>
              </div>
              <p className="text-sm text-center text-muted-foreground">O que quer fazer?</p>
              <div className="flex flex-col gap-2">
                <Button className="w-full gap-2" onClick={() => void executarAcao(placaConfirmada, "cadastrar")}>
                  <Car className="h-4 w-4" />
                  Cadastrar Veículo
                </Button>
                <Button variant="secondary" className="w-full gap-2" onClick={() => void executarAcao(placaConfirmada, "avaliar")}>
                  <ClipboardList className="h-4 w-4" />
                  {vertical.labels.novoRegistro}
                </Button>
                <Button variant="ghost" size="sm" className="w-full gap-1" onClick={iniciarCaptura}>
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Falar de novo
                </Button>
              </div>
            </>
          )}

          {/* BUSCANDO VEÍCULO */}
          {etapa === "buscando_veiculo" && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Buscando veículo...</span>
            </div>
          )}

          {/* CONFIRMANDO VEÍCULO */}
          {etapa === "confirmando_veiculo" && veiculoEncontrado && (
            <>
              <div className="rounded-lg border bg-muted/30 p-3 space-y-0.5">
                <p className="font-semibold">{veiculoEncontrado.marca} {veiculoEncontrado.modelo}</p>
                <p className="text-sm text-muted-foreground">{veiculoEncontrado.ano} · {veiculoEncontrado.cor ?? "—"}</p>
                <p className="text-sm font-mono">{veiculoEncontrado.placa}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button className="w-full gap-2" onClick={() => void confirmarVeiculo(veiculoEncontrado)}>
                  <CheckCircle className="h-4 w-4" />
                  Sim, {vertical.labels.iniciarAcao.toLowerCase()}
                </Button>
                <Button variant="outline" size="sm" className="w-full" onClick={iniciarCaptura}>
                  Não, outra placa
                </Button>
              </div>
            </>
          )}

          {/* CRIANDO */}
          {etapa === "criando_avaliacao" && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Abrindo {vertical.labels.labelPrincipal.toLowerCase()}...</span>
            </div>
          )}

          {/* MANUAL / ERRO */}
          {etapa === "manual" && (
            <>
              {mensagemErro && (
                <p className="text-sm text-destructive text-center">{mensagemErro}</p>
              )}
              <Input
                placeholder="Ex: ABC1D23"
                value={placaManual}
                onChange={e => setPlacaManual(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                maxLength={7}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              <div className="flex flex-col gap-2">
                <Button className="w-full gap-2" size="sm"
                  onClick={() => tentarPlacaManual("cadastrar")} disabled={!placaManual.trim()}>
                  <Car className="h-4 w-4" />
                  Cadastrar
                </Button>
                <Button variant="secondary" className="w-full gap-2" size="sm"
                  onClick={() => tentarPlacaManual("avaliar")} disabled={!placaManual.trim()}>
                  <ClipboardList className="h-4 w-4" />
                  {vertical.labels.novoRegistro}
                </Button>
                <Button variant="ghost" size="sm" className="w-full gap-1" onClick={iniciarCaptura}>
                  <Mic className="h-3.5 w-3.5" />
                  Tentar por voz
                </Button>
              </div>
            </>
          )}

        </div>
      </div>

      {/* Botão mic flutuante (visível nos estados ativos exceto capturando que tem o botão no card) */}
      {etapa !== "capturando" && (
        <button
          onClick={iniciarCaptura}
          aria-label="Capturar por voz de novo"
          className={cn(
            "flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105",
            "bg-primary"
          )}
        >
          <Mic className="h-6 w-6 text-primary-foreground" />
        </button>
      )}
    </div>
  )
}
